import FormGroup from 'components/FormGroup';
import Typography from 'components/Typography/Typography';
import styles from './WebhookSettings.module.scss';
import Button from 'components/Button';
import {useWebhookSettings} from './useWebhookSettings';
import PlusIcon from 'pages/RouteHistory/icons/plus';
import DataTable from 'components/DataTable';
import DeleteModal from 'components/DeleteModal';
import {
  WebhookToggle,
  CreateWebhookModal,
  WebhookDetailModal,
  SecretModal,
} from './components';
import {useCallback, useMemo} from 'react';
import {Webhook} from 'types';
import {format} from 'date-fns';

const localizeEventKey = (eventKey: string) => {
  return eventKey
    .replace(/_/g, ' ')
    .replace(/\./g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const transformData = (
  data: Webhook[],
  handleToggleActive: (id: string, checked: boolean) => Promise<void>
) => {
  return data.map((item) => ({
    ...item,
    events: (
      <div className={styles.eventsList}>
        {item.events.map((event) => (
          <span key={event} className={styles.eventBadge}>
            {localizeEventKey(event)}
          </span>
        ))}
      </div>
    ),
    is_active: (
      <WebhookToggle
        active={item.is_active}
        id={item.id}
        onChangeStatus={handleToggleActive}
      />
    ),
    created_at: format(new Date(item.created_at), 'MMM d, yyyy'),
  }));
};

function WebhookSettings() {
  const {
    WEBHOOK_LIST_SCHEMA,
    isLoadingWebhooks,
    isCreateModalOpen,
    setIsCreateModalOpen,
    createWebhookMutation,
    isCreating,
    data,
    eventOptions,
    handleToggleActive,
    isDetailModalOpen,
    setIsDetailModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedWebhook,
    handleRowClick,
    handleDeleteWebhook,
    deleteWebhookMutation,
    isDeleting,
    regenerateSecretMutation,
    isRegenerating,
    generatedSecret,
    isSecretModalOpen,
    setIsSecretModalOpen,
    createdSecret,
    updateWebhookMutation,
    isUpdating,
  } = useWebhookSettings();

  const onToggle = useCallback(
    async (id: string, checked: boolean) => {
      await handleToggleActive(id, checked);
    },
    [handleToggleActive]
  );

  const transformedData = useMemo(() => {
    return transformData(data || [], onToggle);
  }, [data, onToggle]);

  return (
    <FormGroup columns={1} className={styles.list}>
      <div className={styles.webhookHeader}>
        <Typography variant="label">
          Configure webhooks to receive real-time logs when events occur in your
          account.
        </Typography>
        <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon width={16} height={16} /> Create Webhook
        </Button>
      </div>
      <DataTable
        schema={WEBHOOK_LIST_SCHEMA}
        data={transformedData}
        loading={isLoadingWebhooks}
        selectable={false}
        onClick={handleRowClick}
      />
      {isCreateModalOpen && (
        <CreateWebhookModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={(data) => createWebhookMutation(data)}
          loading={isCreating}
          eventOptions={eventOptions}
        />
      )}
      {isDetailModalOpen && selectedWebhook && (
        <WebhookDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          webhook={selectedWebhook}
          onDelete={handleDeleteWebhook}
          onUpdate={async (data) => {
            await updateWebhookMutation(data);
          }}
          isUpdating={isUpdating}
          onRegenerateSecret={() =>
            regenerateSecretMutation(selectedWebhook.id)
          }
          isRegenerating={isRegenerating}
          generatedSecret={generatedSecret}
          eventOptions={eventOptions}
        />
      )}
      {isSecretModalOpen && createdSecret && (
        <SecretModal
          isOpen={isSecretModalOpen}
          onClose={() => setIsSecretModalOpen(false)}
          secret={createdSecret}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteModal
          openModal={isDeleteModalOpen}
          setOpenModal={setIsDeleteModalOpen}
          onDelete={() => deleteWebhookMutation(selectedWebhook?.id)}
          loading={isDeleting}
          confirmText="Delete"
          text="Are you sure you want to delete this webhook?"
        />
      )}
    </FormGroup>
  );
}

export default WebhookSettings;
